/**
 * @file ErrorMessage — inline validation message
 * @version 0.1.1
 */

import React, { forwardRef, type ElementType, type HTMLAttributes, type ReactNode } from 'react';
import { Icon } from '../Icon';
import styles from './ErrorMessage.module.css';

/*
 * ErrorMessage — inline validation message.
 *
 * A single horizontal row: a 24px alert icon + a short message, both in the
 * accessible magenta used for errors on dark surfaces. Icon and text share one
 * color (the icon inherits via currentColor). Centered by default.
 *
 * Used for form-level validation feedback — e.g. an assessment question screen
 * surfaces "Please select one of the options before continuing." when the user
 * tries to advance without picking an answer.
 *
 *   <ErrorMessage>Please select one of the options before continuing.</ErrorMessage>
 *
 * Non-interactive. Carries role="alert" so assistive tech announces it when it
 * appears; render it conditionally (mount on error) for that announcement to fire.
 */

export interface ErrorMessageProps extends HTMLAttributes<HTMLDivElement> {
  /** The message text. */
  children: ReactNode;
  /** Render the root as a different element/component. Default `'div'`. */
  as?: ElementType;
  /**
   * Surface the message sits on. `dark` (default) uses the pink that reads on
   * dark surfaces; `light` uses the accessible magenta (#C02195). Colour-only.
   */
  theme?: 'light' | 'dark';
}

export const ErrorMessage = forwardRef<HTMLDivElement, ErrorMessageProps>(
  function ErrorMessage({ children, className, as, theme = 'dark', ...rest }, ref) {
  const Component = as ?? 'div';
  const rootClass = [styles.root, theme === 'light' ? styles.light : '', className].filter(Boolean).join(' ');
  return (
    <Component ref={ref} className={rootClass} role="alert" {...rest}>
      <Icon.Standard name="alert" size="md" />
      <span className={styles.text}>{children}</span>
    </Component>
  );
  },
);

ErrorMessage.displayName = 'ErrorMessage';
